/**
 * WeatherService — Météo centralisée partagée entre tous les écrans
 *
 * - Cache AsyncStorage 30 minutes (évite de spammer l'API)
 * - Une seule source de vérité → pas de désalignement entre HomeScreen et LooksScreen
 * - Fallback 18°C si tout échoue
 */
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Location from 'expo-location';

const CACHE_KEY = 'outfit_weather_v1';
const CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes

// Verrou pour éviter les appels simultanés
let _inFlight = null;

/**
 * Retourne la météo actuelle { temperature, weatherCode, city, lat, lon, cachedAt }
 * Mise en cache 30 minutes → un seul appel par session dans la plupart des cas
 */
// v1.12.207 — Override debug : permet de simuler une température arbitraire
// pour tester les propositions par météo (Manteaux dès tempC ≤ 8°C, Vestes
// pour 8-18°C, etc.). Activable :
//   1. Via AsyncStorage : `debug_force_temp` = "5" → override pour ce device
//   2. Via email user dans la liste blanche ci-dessous (test/admin)
const DEBUG_TEMP_USERS = new Set([
  'med.gh07@gmail.com',  // → temp simulée à 5°C (Manteaux activés)
]);

async function _readDebugForcedTemp() {
  try {
    const raw = await AsyncStorage.getItem('debug_force_temp');
    if (raw && /^-?\d+(\.\d+)?$/.test(raw)) {
      const t = parseFloat(raw);
      if (!isNaN(t) && t > -50 && t < 60) return t;
    }
  } catch {}
  // Check email user (cache UserContextService)
  try {
    const m = require('./UserContextService');
    const user = typeof m.getCachedUser === 'function' ? m.getCachedUser() : null;
    const email = (user?.email || '').toLowerCase();
    if (email && DEBUG_TEMP_USERS.has(email)) return 5; // Hiver simulé
  } catch {}
  return null;
}

export async function getCurrentWeather(forceRefresh = false) {
  // Si un appel est déjà en cours, retourner sa promise
  if (_inFlight) return _inFlight;

  // Override debug : si forcé, on ignore le cache et la géoloc.
  const forcedTemp = await _readDebugForcedTemp();
  if (forcedTemp != null) {
    console.log(`[Weather] DEBUG override → ${forcedTemp}°C (cache et géoloc ignorés)`);
    return {
      temperature: forcedTemp,
      weatherCode: forcedTemp <= 8 ? 71 : forcedTemp <= 18 ? 3 : 1, // 71=neige, 3=nuageux, 1=ensoleillé
      city: 'Debug',
      lat: null, lon: null,
      cachedAt: Date.now(),
      _debugOverride: true,
    };
  }

  _inFlight = (async () => {
    try {
      // 1. Lire cache
      if (!forceRefresh) {
        try {
          const raw = await AsyncStorage.getItem(CACHE_KEY);
          if (raw) {
            const cached = JSON.parse(raw);
            const age = Date.now() - (cached.cachedAt || 0);
            if (age < CACHE_TTL_MS && typeof cached.temperature === 'number') {
              console.log(`[Weather] cache hit (age ${Math.round(age/60000)}min): ${cached.temperature}°C ${cached.city || ''}`);
              return cached;
            }
          }
        } catch {}
      }

      // 2. Geolocalisation
      let coords = null;
      try {
        const { status } = await Location.requestForegroundPermissionsAsync();
        if (status === 'granted') {
          const loc = await Location.getCurrentPositionAsync({ accuracy: Location.Accuracy.Low });
          coords = loc.coords;
        }
      } catch(e) { console.warn('[Weather] location:', e.message); }

      if (!coords) {
        console.warn('[Weather] no location, returning fallback 18°C');
        const fallback = { temperature: 18, weatherCode: null, city: null, cachedAt: Date.now() };
        return fallback;
      }

      const { latitude, longitude } = coords;

      // 3. Reverse geocoding (ville + région + pays via GPS = précis, ≠ géoloc IP)
      let city = null, region = null, country = null;
      try {
        const geo = await Location.reverseGeocodeAsync({ latitude, longitude });
        if (geo && geo[0]) {
          city    = geo[0].city || geo[0].subregion || geo[0].region || null;
          region  = geo[0].region || geo[0].subregion || null;
          country = geo[0].country || null;
        }
      } catch {}

      // 4. Fetch météo Open-Meteo
      let temperature = 18;
      let weatherCode = null;
      let isDay = true; // jour par défaut si l'API ne renvoie rien
      try {
        const res = await fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}` +
          `&current=temperature_2m,weather_code,is_day&timezone=auto`
        );
        if (res.ok) {
          const data = await res.json();
          if (data.current) {
            temperature = Math.round(data.current.temperature_2m);
            weatherCode = data.current.weather_code;
            // is_day : 1 = jour, 0 = nuit (Open-Meteo, basé lever/coucher du soleil local)
            if (data.current.is_day !== undefined) isDay = data.current.is_day === 1;
          }
        }
      } catch(e) { console.warn('[Weather] fetch:', e.message); }

      const result = {
        temperature,
        weatherCode,
        isDay,
        city,
        region,
        country,
        lat: latitude,
        lon: longitude,
        cachedAt: Date.now(),
      };

      // 5. Sauvegarder en cache
      try { await AsyncStorage.setItem(CACHE_KEY, JSON.stringify(result)); } catch {}

      console.log(`[Weather] fetched: ${temperature}°C ${city || '(no city)'}`);
      return result;
    } finally {
      // Réinitialiser le verrou après un court délai pour permettre des refresh manuels
      setTimeout(() => { _inFlight = null; }, 1000);
    }
  })();

  return _inFlight;
}

/**
 * Invalider le cache (ex: pull-to-refresh explicite)
 */
export async function invalidateWeatherCache() {
  try { await AsyncStorage.removeItem(CACHE_KEY); } catch {}
}

/**
 * Description météo selon code WMO — v1.12.267 lang-aware via getCurrentLang()
 */
export function getWeatherDesc(code, isDay = true) {
  if (code === undefined || code === null) return '';
  let lang = 'fr';
  try { const { getCurrentLang } = require('../i18n'); lang = getCurrentLang() || 'fr'; } catch {}
  const en = lang === 'en';
  const night = isDay === false;
  // Ciel clair : "Ensoleillé/Sunny" le jour, "Nuit claire/Clear" la nuit.
  if (code === 0) return night ? (en ? 'Clear' : 'Nuit claire') : (en ? 'Sunny' : 'Ensoleillé');
  if (code >= 1 && code <= 3) return en ? 'Partly cloudy' : 'Partiellement nuageux';
  if (code >= 45 && code <= 48) return en ? 'Foggy' : 'Brouillard';
  if (code >= 51 && code <= 67) return en ? 'Rainy' : 'Pluvieux';
  if (code >= 71 && code <= 77) return en ? 'Snowy' : 'Neigeux';
  if (code >= 80 && code <= 82) return en ? 'Showers' : 'Averses';
  if (code === 95 || code === 96 || code === 99) return en ? 'Stormy' : 'Orageux';
  return '';
}
